/**
 * Event Date & Expiration Utilities
 * Ensures events remain active and available throughout the entire event day (until local midnight 23:59:59).
 */

/**
 * Returns today's date in local calendar format "YYYY-MM-DD".
 * @param {Date} [date=new Date()]
 * @returns {string} e.g. "2026-10-30"
 */
export const getLocalDateString = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Extracts the calendar date string "YYYY-MM-DD" from an event's date value.
 * Supports ISO strings (with UTC storage), Date objects, or pre-formatted date strings.
 * @param {string|Date} dateVal
 * @returns {string} e.g. "2026-10-30"
 */
export const getEventCalendarDateString = (dateVal) => {
  if (!dateVal) return "";
  if (typeof dateVal === "string") {
    const match = dateVal.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "";
  // MongoDB stores dates via Date.UTC(y, m-1, d), so UTC year/month/date gives the intended calendar day
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
};

/**
 * Determines whether an event has passed its last date in local timezone.
 * An event is active throughout its entire end date (until local midnight 23:59:59).
 * It is only considered expired when the local calendar day is strictly past the event end date.
 * @param {Object} event
 * @returns {boolean}
 */
export const isEventExpired = (event) => {
  if (!event) return false;
  const rawEndDate = event.endDate || event.startDate;
  if (!rawEndDate) return false;

  const eventDateStr = getEventCalendarDateString(rawEndDate);
  const todayStr = getLocalDateString(new Date());

  if (!eventDateStr || !todayStr) return false;
  return eventDateStr < todayStr;
};

/**
 * Determines whether an event is closed (archived, inactive, cancelled, or expired past its last date).
 * @param {Object} event
 * @returns {boolean}
 */
export const isEventClosed = (event) => {
  if (!event) return false;
  const statusStr = String(event.status || "").toLowerCase();
  const isArchived = statusStr === "archived" || statusStr === "inactive";
  const isCancelled = Boolean(
    event.isCancelled ||
    String(event.lifecycleStatus || "").toUpperCase() === "CANCELLED" ||
    statusStr === "cancelled"
  );
  return isArchived || isCancelled || isEventExpired(event);
};

